import { Stack, Skeleton, Divider } from '@mui/joy'
import { useGate, useStore } from 'effector-react'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  driverModel,
  useDriverMachineInterpreter,
  DriverMachineContext,
  driverMachine,
} from '~/entities/common/Driver'
import { driverChecksModel } from '~/features/common/Drivers/DriverChecks'
import { driverEditModel } from '~/features/common/Drivers/EditDriver'
import { Avatar } from '~/shared/ui/Avatar'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { ChipStatus } from '~/shared/ui/Chips'
import { Container, PageContainer, PageTitle } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import RoutesTabs from '~/shared/ui/RoutesTabs'
import { rentalContractCreatedControlModel } from '~/widgets/common/Driver/RentalContractControls/RentalContractCreated'
import { ActionButtons } from './ui/ActionButtons'
import { Alerts } from './ui/Alerts'
import { ExperienceDays } from './ui/ExperienceDays'
import { MainBalance } from './ui/MainBalance'
import { useConfigTabs } from './useConfigTabs'

type DriverPage = {
  detailsPage: ReactNode
}

export function DriverPage({ detailsPage }: DriverPage) {
  const { t } = useTranslation()

  const params = useParams<{ driverId: string }>()
  const driverId = params.driverId as string

  useGate(driverModel.Gate, { id: driverId })
  useGate(rentalContractCreatedControlModel.GetRentalContractActiveGate)
  useGate(driverChecksModel.Gate, { driverId })
  useGate(driverEditModel.Gate, { driverId })

  const configTabs = useConfigTabs({ driverId, detailsPage })
  const driver = useStore(driverModel.$driver)
  const isLoading = useStore(driverModel.requestDriverFx.pending)

  const driverFullName = driver?.getFullName()
  const driverStatus = driver?.getStatus()
  const photoOriginalUrlDownload = driver?.getPhotoOriginalUrlDownload()

  const driverMachineInterpreter = useDriverMachineInterpreter(driverStatus)
  const driverMachineCtxValue = useMemo(
    () => ({ interpreter: driverMachineInterpreter }),
    [driverMachineInterpreter],
  )
  const statusNode = driverStatus
    ? driverMachine.getStateNode(driverStatus).meta
    : null

  const title = `${t('driver card')} ${isLoading ? '...' : driverFullName}`

  const breadcrumbs = useMemo(
    (): BreadcrumbsList => [
      {
        titleI18n: 'drivers',
        link: '/drivers',
      },
      { title: driverFullName || '...' },
    ],
    [driverFullName],
  )

  const driverError = driverModel.useDriverError(driverId)
  if (driverError) {
    return <NotFound message={t('alert.failed to load the driver')} />
  }

  return (
    <DriverMachineContext.Provider value={driverMachineCtxValue}>
      <PageContainer title={title}>
        <RoutesTabs
          parentPath={`/drivers/${driverId}`}
          config={configTabs}
          parentContent={
            <Container isHeader>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
              >
                <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />
                <Stack
                  direction='row'
                  divider={<Divider orientation='vertical' />}
                  spacing={2}
                >
                  <MainBalance />
                  <ExperienceDays />
                </Stack>
              </Stack>

              <ChipStatus
                data-testid='driver-status'
                variant={statusNode?.color || 'gray'}
                loading={
                  isLoading && (
                    <Skeleton sx={{ top: 0, left: 0, minWidth: '70px' }} />
                  )
                }
                labelI18n={statusNode?.label || ''}
                sx={{ minWidth: '70px', marginBottom: '6px' }}
              />

              <Stack
                direction='row'
                alignItems='end'
                justifyContent='space-between'
              >
                <Stack direction='row' alignItems='center' spacing={2}>
                  <Avatar
                    url={photoOriginalUrlDownload}
                    isLoading={isLoading}
                  />
                  <PageTitle loading={isLoading}>{driverFullName}</PageTitle>
                </Stack>
                <ActionButtons driverId={driverId} loading={isLoading} />
              </Stack>
              <br />
              <Alerts driverId={driverId} />
            </Container>
          }
        />
      </PageContainer>
    </DriverMachineContext.Provider>
  )
}
